<script setup lang="ts">
import { ref, watch } from 'vue'

import FormInput from '@tix/ds/components/atoms/FormInput.vue'
import type { FormInputExposed } from '@tix/ds/components/atoms/form-input/types.ts'

import { type DonationOptionsValues, type DonationOptionsVisibility } from '@/helpers/donationSurvey'

import BaseCard from '@/components/BaseCard.vue'

type Props = {
  title?: string
  visibility: DonationOptionsVisibility
}

const model = defineModel<DonationOptionsValues>({ required: true })

const props = withDefaults(defineProps<Props>(), {
  title: 'Donation options',
})

watch(
  () => model.value.dedicate,
  (value) => {
    if (!value) {
      model.value.honoreeFirstName = ''
      model.value.honoreeLastName = ''
    }
  },
)

const honoreeFirstNameRef = ref<FormInputExposed | null>(null)
const honoreeLastNameRef = ref<FormInputExposed | null>(null)

function validateAndReturnError() {
  if (!model.value.dedicate) return null

  const firstNameError = honoreeFirstNameRef.value?.validateAndReturnError()
  const lastNameError = honoreeLastNameRef.value?.validateAndReturnError()
  return firstNameError ?? lastNameError ?? null
}

defineExpose({ validateAndReturnError })
</script>

<template>
  <div v-if="visibility !== null" class="donation-options">
    <h2 v-if="title">{{ props.title }}</h2>

    <template v-if="visibility === 'dedicate' || visibility === 'both'">
      <BaseCard :class="{ checked: model.dedicate }">
        <FormInput
          id="donation-dedication"
          v-model="model.dedicate"
          description="Honor someone special by dedicating this donation with a personal message"
          label="Dedication"
          type="checkbox"
        />
      </BaseCard>

      <div v-if="model.dedicate" class="honoree-name-inputs">
        <FormInput
          id="honoree-first-name"
          ref="honoreeFirstNameRef"
          v-model="model.honoreeFirstName"
          class="honoree-name-input"
          label="Honoree's first name"
          required
          type="string"
        />
        <FormInput
          id="honoree-last-name"
          ref="honoreeLastNameRef"
          v-model="model.honoreeLastName"
          class="honoree-name-input"
          label="Honoree's last name"
          required
          type="string"
        />
      </div>
    </template>

    <BaseCard v-if="visibility === 'anonymous' || visibility === 'both'" :class="{ checked: model.anonymous }">
      <FormInput
        id="donation-anonymity"
        v-model="model.anonymous"
        description="Your name will not appear in donor lists or be shared publicly. We will still need your information for payment purposes."
        label="Make this donation anonymous"
        type="checkbox"
      />
    </BaseCard>
  </div>
</template>

<style scoped>
.donation-options {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

h2 {
  font-size: var(--font-size-h3);
}

.base-card {
  &.checked {
    background-color: var(--action-card-bg-active, #001d3e);
    border-color: var(--action-card-border-active, #0074c7);
  }
}

.form-input.checkbox {
  & :deep(label) {
    font-weight: var(--font-weight-semibold);
  }

  & :deep(.description) {
    position: relative;
    /* TODO: Fix this in FormInput and support different input sizes */
    left: -35px;
    margin-top: var(--space-0-5);
  }
}

.honoree-name-inputs {
  display: flex;
  flex-direction: column;
  gap: var(--space-1-5);
  padding: 0 var(--space-1-5);
}
</style>
